import { globalGetService } from '../../../utils/globalApiServices';
import axiosInstance from '../../../utils/apiInterceptor';
import { checkApiStatus } from '../../../utils';

// Normalises the session list into the { list, pagination } shape Pagination expects
export const getUploadSessionsApi = ({ page = 1, per_page = 20 } = {}) =>
  globalGetService('records/v4/data-room/uploads/', { page, limit: per_page })
    .then(response => {
      if (!checkApiStatus(response)) {
        return { ok: false, message: response && response.data ? response.data.message : '', list: [], pagination: null };
      }
      const data = response.data.data || {};
      return {
        ok: true,
        list: data.results || [],
        pagination: { page: data.page || page, per_page: data.limit || per_page, total: data.count || 0 },
      };
    });

// These calls must reject on failure, so they use axiosInstance (the global*Service helpers always resolve)

// The app's axios instance has no timeout, so a request cut off by sleep or a dropped connection could
// wait forever and stall the upload. Timed out requests fail and are retried like any other error.
const REQUEST_TIMEOUT_MS = 60 * 1000;
// Registering 500 files can take longer
const REGISTER_TIMEOUT_MS = 2 * 60 * 1000;
const timeout = { timeout: REQUEST_TIMEOUT_MS };

const uploadsUrl = (assetType, assetSlug) => `records/v4/${assetType}/${assetSlug}/data-room/uploads/`;
const sessionUrl = (assetType, assetSlug, uploadId) => `${uploadsUrl(assetType, assetSlug)}${uploadId}/`;
const uploadFilesUrl = (assetType, assetSlug, uploadId) => `${sessionUrl(assetType, assetSlug, uploadId)}files/`;

// Turns a failed response into an error carrying the server's message and data (e.g. missing_parts)
const unwrap = (response) => {
  if (!checkApiStatus(response)) {
    const serverMessage = (response && response.data && response.data.message) || null;
    const error = new Error(serverMessage || 'Could not reach the server');
    error.serverMessage = serverMessage;
    error.data = response && response.data ? response.data.data : null;
    throw error;
  }
  return response.data.data || {};
};

export const getErrorMessage = (error, fallback) => (error && error.serverMessage) || fallback;

export const createUploadSessionApi = ({ assetType, assetSlug, folderUuid, totalFiles, totalBytes }) =>
  axiosInstance.post(uploadsUrl(assetType, assetSlug), { folder_uuid: folderUuid, total_files: totalFiles, total_bytes: totalBytes })
    .then(unwrap);

export const UPLOAD_TYPE = { SINGLE: 1, MULTIPART: 2 };

// The server stores names and paths in NFC; macOS may give accented letters in NFD
export const toNFC = (text) => (text || '').normalize('NFC');

// Mirrors the server's path normalization (slashes, whitespace, NFC) so local and server paths compare equal
export const normalizeUploadPath = (path) => (path || '')
  .normalize('NFC')
  .replace(/\\/g, '/')
  .split('/')
  .map(name => name.replace(/\s+/g, ' ').trim())
  .filter(Boolean)
  .join('/');

// rejected / skipped entries may be plain strings or objects with a reason
const describe = (entry, idKeys) => {
  if (typeof entry === 'string') return { id: entry, reason: null };
  const key = idKeys.find(k => entry && entry[k]);
  return { id: key ? entry[key] : null, reason: entry ? entry.reason || entry.error || entry.message || null : null };
};

// POST .../files/  ->  { files: [{ fileId, path, name, status }], rejected: [{ id, reason }] }
export const registerUploadFilesApi = ({ assetType, assetSlug, uploadId, files }) =>
  axiosInstance.post(uploadFilesUrl(assetType, assetSlug, uploadId), { files }, { timeout: REGISTER_TIMEOUT_MS })
    .then(unwrap)
    .then(data => ({
      files: (data.files || []).map(file => ({ fileId: file.file_id, path: file.path, name: file.name, status: file.status })),
      rejected: (data.rejected || []).map(entry => describe(entry, ['path', 'name'])),
    }));

// POST .../files/urls/  ->  { urls: { [fileId]: info }, skipped }
// SINGLE (< 100 MB): { url, method, headers, expiresAt }; headers must be sent unchanged on the PUT
// MULTIPART: { partSize, totalParts, partsDone }; part URLs come from getUploadPartUrlsApi
export const getUploadFileUrlsApi = ({ assetType, assetSlug, uploadId, fileIds }) =>
  axiosInstance.post(`${uploadFilesUrl(assetType, assetSlug, uploadId)}urls/`, { fileIds }, timeout)
    .then(unwrap)
    .then(data => {
      const urls = {};
      (data.urls || []).forEach(item => {
        if (!item.file_id) return;
        if (Number(item.upload_type) === UPLOAD_TYPE.MULTIPART) {
          urls[item.file_id] = {
            uploadType: UPLOAD_TYPE.MULTIPART,
            partSize: Number(item.part_size),
            totalParts: Number(item.total_parts),
            partsDone: (item.parts_done || []).map(Number),
          };
        } else if (item.signed_url) {
          urls[item.file_id] = {
            uploadType: UPLOAD_TYPE.SINGLE,
            url: item.signed_url,
            method: item.method || 'PUT',
            headers: item.headers || null,
            expiresAt: item.expires_at ? Date.parse(item.expires_at) : null,
          };
        }
      });
      return { urls, skipped: (data.skipped || []).map(entry => describe(entry, ['file_id', 'fileId', 'id'])) };
    });

// POST .../files/{fileId}/parts/  ->  { [partNumber]: { url, expiresAt } }
export const getUploadPartUrlsApi = ({ assetType, assetSlug, uploadId, fileId, partNumbers }) =>
  axiosInstance.post(`${uploadFilesUrl(assetType, assetSlug, uploadId)}${fileId}/parts/`, { part_numbers: partNumbers }, timeout)
    .then(unwrap)
    .then(data => {
      const urls = {};
      (data.urls || []).forEach(item => {
        if (item.part_number && item.signed_url) {
          urls[Number(item.part_number)] = { url: item.signed_url, expiresAt: item.expires_at ? Date.parse(item.expires_at) : null };
        }
      });
      return urls;
    });

const SESSION_FILES_PAGE_SIZE = 500;

const toSessionFile = (file) => ({
  fileId: file.file_id,
  name: file.name,
  path: file.path,
  size: Number(file.file_size),
  status: file.status,
  uploadType: Number(file.upload_type),
  uploadedBytes: Number(file.uploaded_bytes) || 0,
  errorMessage: file.error_message || '',
  updatedAt: file.updated_at || null,
});

// GET .../files/?limit=&cursor=&status=  ->  one page: { files, count, nextCursor }
export const getUploadSessionFilesPageApi = ({ assetType, assetSlug, uploadId, status, cursor = null, limit = SESSION_FILES_PAGE_SIZE }) =>
  axiosInstance.get(uploadFilesUrl(assetType, assetSlug, uploadId), {
    ...timeout,
    params: { limit, ...(status ? { status } : {}), ...(cursor !== null ? { cursor } : {}) },
  })
    .then(unwrap)
    .then(data => ({
      files: (data.results || []).map(toSessionFile),
      count: data.count !== undefined ? Number(data.count) : null,
      nextCursor: data.next_cursor !== undefined ? data.next_cursor : null,
    }));

// Every page of the session's files; rejects if paging stops advancing
export const getUploadSessionFilesApi = async ({ assetType, assetSlug, uploadId, status }) => {
  const files = [];
  const seenCursors = new Set();
  let cursor = null;
  do {
    const page = await getUploadSessionFilesPageApi({ assetType, assetSlug, uploadId, status, cursor });
    files.push(...page.files);
    cursor = page.nextCursor;
    if (cursor !== null && seenCursors.has(cursor)) throw new Error('Upload file list paging did not advance');
    if (cursor !== null) seenCursors.add(cursor);
    if (page.count !== null && files.length > page.count) throw new Error('Upload file list returned more files than its count');
  } while (cursor !== null);
  return files;
};

// Marks a file COMPLETED or FAILED. A multipart complete may fail with data.missing_parts
export const updateUploadFileStatusApi = ({ assetType, assetSlug, uploadId, fileId, status, errorMessage }) =>
  axiosInstance.put(`${uploadFilesUrl(assetType, assetSlug, uploadId)}${fileId}/`, { status, ...(errorMessage ? { error_message: errorMessage } : {}) }, timeout)
    .then(unwrap);

// Discards a file's uploaded data so a changed file starts over
export const restartUploadFileApi = ({ assetType, assetSlug, uploadId, fileId, fileSize }) =>
  axiosInstance.put(`${uploadFilesUrl(assetType, assetSlug, uploadId)}${fileId}/`, { restart: true, file_size: fileSize }, timeout)
    .then(unwrap);

// Closes a session early. The server refuses while any file is PENDING or UPLOADING
export const completeUploadSessionApi = ({ assetType, assetSlug, uploadId }) =>
  axiosInstance.put(sessionUrl(assetType, assetSlug, uploadId), { status: 'COMPLETED' }, timeout)
    .then(unwrap);

// Heartbeat: refreshes the file's updated_at so others can tell the upload is still running
export const reportUploadFileProgressApi = ({ assetType, assetSlug, uploadId, fileId, uploadedBytes }) =>
  axiosInstance.put(`${uploadFilesUrl(assetType, assetSlug, uploadId)}${fileId}/`, { uploaded_bytes: uploadedBytes }, timeout)
    .then(unwrap);

// Large selections are checked in batches to stay under the proxy timeout
const NAME_CHECK_SINGLE_REQUEST_MAX = 2000;
const NAME_CHECK_BATCH_SIZE = 500;

// POST records/v2/file-name-checker/{folderUuid}/  ->  { overwrite_files, improper_files, file_naming_conventions }
export const checkUploadFileNamesApi = async ({ folderUuid, items }) => {
  const batchSize = items.length > NAME_CHECK_SINGLE_REQUEST_MAX ? NAME_CHECK_BATCH_SIZE : items.length;
  const result = { overwrite_files: [], improper_files: [], file_naming_conventions: [] };
  for (let offset = 0; offset < items.length; offset += batchSize) {
    const files = items.slice(offset, offset + batchSize).map(({ file, relativePath }) => ({ name: toNFC(file.name), path: relativePath }));
    const data = await axiosInstance.post(`records/v2/file-name-checker/${folderUuid}/`, { files }).then(unwrap);
    result.overwrite_files.push(...(data.overwrite_files || []));
    result.improper_files.push(...(data.improper_files || []));
    if (!result.file_naming_conventions.length) result.file_naming_conventions = data.file_naming_conventions || [];
  }
  return result;
};

// Lowers a session's totals when a resume is a few files short, so it can still complete
export const updateUploadSessionTotalsApi = ({ assetType, assetSlug, uploadId, totalFiles, totalBytes }) =>
  axiosInstance.put(sessionUrl(assetType, assetSlug, uploadId), { total_files: totalFiles, total_bytes: totalBytes })
    .then(unwrap);
